import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, Clock, User, ArrowRight, ArrowLeft, CalendarPlus } from "lucide-react";
import { getSessionApi } from "../../../utils/demoApi";
import { AppButton } from "../../ui";
import { resolveSessionKind } from "../sessionKind";
import DemoPublicShell from "./DemoPublicShell";
import useAttendeeToken from "./useAttendeeToken";
import DemoRegisterForm, { DEMO_REGISTER_FORM_ID } from "./DemoRegisterForm";
import "../demo-classes.scss";

const formatDateTime = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch { return String(d); }
};

const stateMessages = (vocab) => ({
  ended: `This ${vocab.noun} has ended. Thanks for your interest!`,
  cancelled: `This ${vocab.noun} has been cancelled.`,
});

const DemoLandingPage = ({ initialView = "details", kind = "demo" }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const vocab = resolveSessionKind(kind);
  const api = getSessionApi(vocab.kind);
  // Absorbs `?t=` if this page itself was reached from a resume link.
  const attendeeToken = useAttendeeToken(vocab.kind, slug);
  const STATE_MESSAGES = stateMessages(vocab);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState(initialView);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getSession(slug)
      .then(({ data }) => setSession(data))
      .catch((err) => {
        if (err?.response?.status === 404) setError(`${vocab.FullNoun} not found.`);
        else if (err?.response?.status === 410) setError(`This ${vocab.noun} has been cancelled.`);
        else setError(`Could not load this ${vocab.noun}. Please try again later.`);
      });
    // `api` and `vocab` are derived from `kind` and stable for a given mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, kind]);

  /**
   * A returning visitor never sees this form again.
   *
   * Hand off to /join rather than deciding here: that page owns the one
   * definition of "where does this person belong right now", including
   * verifying the credential is still alive. This used to bounce straight to
   * the lobby on the mere PRESENCE of a stored token, so an expired one sent
   * people to a lobby that then failed its own check and bounced them back.
   *
   * No loop: /join with a dead token clears it and returns them to /register,
   * by which point there is nothing here left to redirect on.
   */
  useEffect(() => {
    if (attendeeToken) {
      navigate(`${vocab.publicBase}/${slug}/join`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate, kind, attendeeToken]);

  if (error) {
    return (
      <DemoPublicShell split kind={vocab.kind}>
        <div className="demo-split__view">
          <h2 className="demo-split__title">Unavailable</h2>
          <p className="demo-split__desc">{error}</p>
        </div>
      </DemoPublicShell>
    );
  }

  if (!session) {
    return (
      <DemoPublicShell split kind={vocab.kind}>
        <div className="demo-split__view">
          <p className="demo-public__pulse">Loading {vocab.noun} details...</p>
        </div>
      </DemoPublicShell>
    );
  }

  if (session.status === "ended" || session.status === "cancelled") {
    return (
      <DemoPublicShell split kind={vocab.kind} institute={session.institute}>
        <div className="demo-split__view">
          <h2 className="demo-split__title">{session.title}</h2>
          <p className="demo-split__desc">{STATE_MESSAGES[session.status]}</p>
        </div>
      </DemoPublicShell>
    );
  }

  const isGuest = session.registrationMode === "guest";

  const heroProps = {
    title: session.title || vocab.copy.heroTitle,
    subtitle: session.description || vocab.copy.landingSubtitle,
  };

  const detailsFooter = (
    <>
      <AppButton variant="outline" as="a" href={api.calendarUrl(slug)}>
        <CalendarPlus size={16} /> Add to calendar
      </AppButton>
      <AppButton
        variant="default"
        onClick={() => setView("register")}
        style={{ marginLeft: "auto" }}
      >
        {isGuest ? "Join as guest" : "Register to join"} <ArrowRight size={16} />
      </AppButton>
    </>
  );

  const registerFooter = (
    <>
      <AppButton variant="outline" onClick={() => setView("details")}>
        <ArrowLeft size={16} /> Back
      </AppButton>
      <AppButton
        variant="default"
        type="submit"
        form={DEMO_REGISTER_FORM_ID}
        disabled={submitting}
        style={{ marginLeft: "auto" }}
      >
        {submitting
          ? (isGuest ? "Joining..." : "Registering...")
          : <>{isGuest ? "Join now" : "Confirm registration"} <ArrowRight size={16} /></>}
      </AppButton>
    </>
  );

  return (
    <DemoPublicShell
      split
      kind={vocab.kind}
      institute={session.institute}
      hero={heroProps}
      footer={view === "details" ? detailsFooter : registerFooter}
    >
      {view === "details" ? (
        <div className="demo-split__view">
          <h2 className="demo-split__title">{session.title}</h2>
          {session.description && (
            <p className="demo-split__desc">{session.description}</p>
          )}

          <div className="demo-split__meta">
            <div className="demo-split__meta-row">
              <CalendarClock size={18} />
              <div>
                <p className="demo-split__meta-row-label">Starts</p>
                <p className="demo-split__meta-row-value">{formatDateTime(session.scheduledStartAt)}</p>
              </div>
            </div>
            {session.timezone && (
              <div className="demo-split__meta-row">
                <Clock size={18} />
                <div>
                  <p className="demo-split__meta-row-label">Timezone</p>
                  <p className="demo-split__meta-row-value">{session.timezone}</p>
                </div>
              </div>
            )}
            {session.instructorName && (
              <div className="demo-split__meta-row">
                <User size={18} />
                <div>
                  <p className="demo-split__meta-row-label">Hosted by</p>
                  <p className="demo-split__meta-row-value">{session.instructorName}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <DemoRegisterForm
          session={session}
          slug={slug}
          kind={vocab.kind}
          onSubmittingChange={setSubmitting}
          onRegistered={() => navigate(`${vocab.publicBase}/${slug}/lobby`, { replace: true })}
        />
      )}
    </DemoPublicShell>
  );
};

export default DemoLandingPage;
